// src/api/features/promos/api.ts

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  Promo,
  PromoWithConfig,
  PromoAnalytics,
  PromoListQuery,
  PaginatedPromos,
  CreatePromoPayload,
  UpdatePromoPayload,
  GenerateCodeBatchPayload,
  GenerateCodeBatchResult,
  SimulatePayload,
  SimulateResult,
  PromoCustomerLite,
} from "./types";

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

const BASE = PATHS.promos.base;

export async function listPromos(query: PromoListQuery = {}): Promise<PaginatedPromos> {
  const page = Math.max(1, Number(query.page ?? 1));
  const pageSize = Math.max(1, Math.min(100, Number(query.pageSize ?? 20)));
  const url = `${BASE}${qs({
    page,
    pageSize,
    status: query.status,
    search: query.search,
    includeDeleted: query.includeDeleted ? "true" : undefined,
  })}`;

  const res = await request<PaginatedPromos | (ApiEnvelope<Promo[]> & { meta?: any })>({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<Promo[]> & { meta?: any };
    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};
    return {
      items,
      page: Number(meta.page ?? page),
      pageSize: Number(meta.pageSize ?? pageSize),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }
  return res as PaginatedPromos;
}

export async function getPromo(id: number | string): Promise<PromoWithConfig> {
  const res = await request<PromoWithConfig | ApiEnvelope<PromoWithConfig>>({
    method: "GET",
    url: `${BASE}/${id}`,
  });
  return unwrap<PromoWithConfig>(res);
}

export async function getPromoAnalytics(id: number | string): Promise<PromoAnalytics> {
  const res = await request<PromoAnalytics | ApiEnvelope<PromoAnalytics>>({
    method: "GET",
    url: `${BASE}/${id}/analytics`,
  });
  return unwrap<PromoAnalytics>(res);
}

export async function createPromo(payload: CreatePromoPayload): Promise<Promo> {
  const res = await request<Promo | ApiEnvelope<Promo>>({
    method: "POST",
    url: BASE,
    data: payload,
  });
  return unwrap<Promo>(res);
}

export async function updatePromo(
  id: number | string,
  payload: UpdatePromoPayload
): Promise<Promo> {
  const res = await request<Promo | ApiEnvelope<Promo>>({
    method: "PATCH",
    url: `${BASE}/${id}`,
    data: payload,
  });
  return unwrap<Promo>(res);
}

export async function deletePromo(id: number | string): Promise<{ id: number; deleted: boolean } | void> {
  const res = await request<any>({ method: "DELETE", url: `${BASE}/${id}` });
  if (!res) return;
  return unwrap(res);
}

export async function generatePromoCodes(
  id: number | string,
  payload: GenerateCodeBatchPayload
): Promise<GenerateCodeBatchResult> {
  const res = await request<GenerateCodeBatchResult | ApiEnvelope<GenerateCodeBatchResult>>({
    method: "POST",
    url: `${BASE}/${id}/codes`,
    data: payload,
  });
  return unwrap<GenerateCodeBatchResult>(res);
}

export async function searchPromoCustomers(q: string): Promise<PromoCustomerLite[]> {
  const res = await request<PromoCustomerLite[] | ApiEnvelope<PromoCustomerLite[]>>({
    method: "GET",
    url: `${BASE}/customer-search${qs({ q })}`,
  });
  const data = unwrap<PromoCustomerLite[]>(res);
  return Array.isArray(data) ? data : [];
}

export async function simulatePromo(
  id: number | string,
  payload: SimulatePayload
): Promise<SimulateResult> {
  const res = await request<SimulateResult | ApiEnvelope<SimulateResult>>({
    method: "POST",
    url: `${BASE}/${id}/simulate`,
    data: payload,
  });
  return unwrap<SimulateResult>(res);
}
